import { useState, useCallback, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { useAuth } from "@/providers/AuthProvider";
import Button from "@/components/ui/Button";
import Icon from "@/components/ui/Icon";

import step1Img from "@/assets/shapes/onboarding-step1.png";
import step2Img from "@/assets/shapes/onboarding-step2.png";
import step3Img from "@/assets/shapes/onboarding-step3.png";
import step4Img from "@/assets/shapes/onboarding-step4.png";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

const STEPS = [
    { image: step1Img, label: "Step 1", title: "Set up a coin with\n1 SOL deposit" },
    { image: step2Img, label: "Step 2", title: "Invite Friends to\nlaunch together" },
    { image: step3Img, label: "Step 3", title: "Hold Together till\ncoin graduates" },
    { image: step4Img, label: "Step 4", title: "Share fees & win\ntogether" },
];

/* ════════════════════════════════════════════
   Onboarding Page
   ════════════════════════════════════════════ */
export default function OnboardingPage(): ReactNode {
    const [step, setStep] = useState(0);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const posthog = usePostHog();
    const { token } = useAuth();

    const next = useCallback(() => {
        if (step < STEPS.length - 1) {
            setStep((s) => s + 1);
        } else {
            completeOnboarding();
        }
    }, [step]);

    const skip = useCallback(() => {
        posthog.capture("onboarding_skipped", { skippedAtStep: step + 1 });
        completeOnboarding();
    }, [step]);

    async function completeOnboarding() {
        if (token) {
            try {
                await fetch(`${API_BASE}/api/users/onboarding`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
            } catch {
                // silently fail — user can still proceed
            }
        }
        posthog.capture("onboarding_completed");
        localStorage.setItem("codev_onboarded", "true");
        const returnTo = searchParams.get("returnTo") || "/";
        navigate(returnTo, { replace: true });
    }

    const current = STEPS[step];

    return (
        <div className="fixed inset-0 flex flex-col overflow-hidden max-w-[430px] mx-auto bg-[#0a0a0a]">
            {/* Back & Skip buttons — top row */}
            <div className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center p-[clamp(16px,2.6dvh,24px)]">
                {step > 0 ? (
                    <button
                        onClick={() => setStep((s) => s - 1)}
                        className="text-white/60 hover:text-white transition-colors"
                    >
                        <Icon name="back-arrow" size={24} />
                    </button>
                ) : (
                    <div />
                )}
                <button
                    onClick={skip}
                    className="text-[clamp(16px,1.9dvh,18px)] font-semibold text-white/60 hover:text-white transition-colors"
                    style={{ fontFamily: "var(--font-display)" }}
                >
                    Skip
                </button>
            </div>

            {/* Illustration — fills upper portion */}
            <div className={`relative z-10 flex-1 flex items-center justify-center min-h-0 ${
                step === 3 ? "px-0 pt-[clamp(24px,4dvh,40px)]" : "px-5 pt-[clamp(48px,8dvh,72px)]"
            }`}>
                <img
                    key={step}
                    src={current.image}
                    alt={current.label}
                    className={`max-h-full object-contain animate-[fadeIn_0.4s_ease-out] ${
                        step === 3 ? "max-w-[110%] w-[110%]" : "max-w-full"
                    }`}
                />
            </div>

            {/* Bottom section — dots + text + button */}
            <div className="relative z-10 shrink-0 flex flex-col items-center px-[clamp(16px,2.6dvh,24px)] pb-[clamp(24px,3.4dvh,40px)]">
                {/* Step dots */}
                <StepDots current={step} total={STEPS.length} onGoTo={setStep} />

                {/* Step label */}
                <p
                    className="text-white/50 text-[14px] font-semibold mt-[clamp(12px,1.7dvh,20px)]"
                    style={{ fontFamily: "var(--font-display)" }}
                >
                    {current.label}
                </p>

                {/* Title */}
                <h1
                    className="text-white text-[clamp(24px,3dvh,28px)] leading-[1.2] font-bold text-center mt-[clamp(8px,1dvh,12px)] whitespace-pre-line"
                    style={{ fontFamily: "var(--font-display)" }}
                >
                    {current.title}
                </h1>

                {/* Next button */}
                <Button
                    variant="cta"
                    size="lg"
                    textStyle="v2"
                    fullWidth
                    onClick={next}
                    className="mt-[clamp(20px,3dvh,32px)] text-[clamp(16px,1.9dvh,18px)]"
                >
                    {step === STEPS.length - 1 ? "Get Started" : "Next"}
                </Button>
            </div>

            {/* Bottom amber/orange gradient glow */}
            <div className="absolute bottom-0 left-0 right-0 h-[45%] pointer-events-none z-0">
                {/* Main warm glow blob */}
                <div
                    className="absolute bottom-[-20%] left-1/2 -translate-x-1/2 w-[120%] aspect-square rounded-full"
                    style={{
                        background: "radial-gradient(circle, rgba(255,180,50,0.5) 0%, rgba(220,130,20,0.3) 30%, rgba(180,90,10,0.1) 55%, transparent 70%)",
                        filter: "blur(40px)",
                    }}
                />
                {/* Secondary golden highlight — tighter, brighter */}
                <div
                    className="absolute bottom-[-10%] left-1/2 -translate-x-1/2 w-[80%] aspect-[2/1] rounded-full"
                    style={{
                        background: "radial-gradient(ellipse, rgba(255,214,81,0.45) 0%, rgba(255,170,40,0.2) 40%, transparent 70%)",
                        filter: "blur(30px)",
                    }}
                />
            </div>
        </div>
    );
}

/* ════════════════════════
   Step Indicator Dots
   ════════════════════════ */
function StepDots({ current, total, onGoTo }: { current: number; total: number; onGoTo: (step: number) => void }): ReactNode {
    return (
        <div className="flex gap-[4px]">
            {Array.from({ length: total }, (_, i) => {
                const isActive = i === current;
                return (
                    <button
                        key={i}
                        type="button"
                        aria-label={`Go to step ${i + 1}`}
                        onClick={() => onGoTo(i)}
                        className={`h-[6px] rounded-full transition-all duration-300 cursor-pointer ${
                            isActive
                                ? "w-[38px] bg-white"
                                : "w-[12px] bg-white/30"
                        }`}
                    />
                );
            })}
        </div>
    );
}
